// =============================================================================
// Origin UI — src/pages/employees/VisaTab.tsx
// Visa & Permits tab on the Employee Profile page.
// Batch 3.3 — Onboarding Lifecycle module (WS-3 Visa Tracking).
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import styles from './employees.module.css';

// ─── Types ───

interface VisaDto {
  employeeVisaId: string;
  employeeId: string;
  visaType: string;
  visaNumber: string | null;
  issuingCountry: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  status: string;
  linkedDocumentId: string | null;
  linkedDocumentFileName: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

interface CreateVisaDto {
  visaType: string;
  visaNumber: string | null;
  issuingCountry: string | null;
  issueDate: string | null;
  expiryDate: string | null;
  status: string | null;
  linkedDocumentId: string | null;
  notes: string | null;
}

const VISA_STATUSES = ['Active', 'Expiring', 'Expired', 'Renewal', 'Cancelled'];

function getExpiryBadge(expiryDate: string | null, status: string): { colour: string; label: string } {
  if (status === 'Cancelled') return { colour: '#6B7280', label: 'Cancelled' };
  if (status === 'Expired' || status === 'Renewal') return { colour: '#DC2626', label: status };
  if (!expiryDate) return { colour: '#059669', label: status };

  const days = Math.floor((new Date(expiryDate).getTime() - Date.now()) / 86400000);
  if (days < 0) return { colour: '#DC2626', label: 'Expired' };
  if (days <= 90) return { colour: '#F59E0B', label: `${days}d remaining` };
  return { colour: '#059669', label: 'Active' };
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ─── Component ───

export function VisaTab({ employeeId, isHR }: { employeeId: string; isHR: boolean }) {
  const [visas, setVisas] = useState<VisaDto[]>([]);
  const [visaTypes, setVisaTypes] = useState<{ visaDocumentTypeId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // Form state
  const emptyForm: CreateVisaDto = {
    visaType: '',
    visaNumber: null,
    issuingCountry: null,
    issueDate: null,
    expiryDate: null,
    status: 'Active',
    linkedDocumentId: null,
    notes: null,
  };
  const [form, setForm] = useState<CreateVisaDto>(emptyForm);

  useEffect(() => { load(); }, [employeeId]);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const [data, types] = await Promise.all([
        api.get<VisaDto[]>(`/api/employees/${employeeId}/visas`),
        api.get<{ visaDocumentTypeId: number; name: string }[]>('/api/reference/visa-document-types'),
      ]);
      setVisas(data);
      setVisaTypes(types);
    } catch (err: any) {
      setError(err.message || 'Failed to load visas');
    } finally {
      setLoading(false);
    }
  }

  function startAdd() {
    setForm(emptyForm);
    setEditingId(null);
    setShowAdd(true);
  }

  function startEdit(visa: VisaDto) {
    setForm({
      visaType: visa.visaType,
      visaNumber: visa.visaNumber,
      issuingCountry: visa.issuingCountry,
      issueDate: visa.issueDate ? visa.issueDate.split('T')[0] : null,
      expiryDate: visa.expiryDate ? visa.expiryDate.split('T')[0] : null,
      status: visa.status,
      linkedDocumentId: visa.linkedDocumentId,
      notes: visa.notes,
    });
    setEditingId(visa.employeeVisaId);
    setShowAdd(false);
    setExpandedId(visa.employeeVisaId);
  }

  async function handleSave() {
    if (!form.visaType.trim()) {
      setError('Visa type is required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (editingId) {
        await api.put(`/api/employees/${employeeId}/visas/${editingId}`, form);
      } else {
        await api.post(`/api/employees/${employeeId}/visas`, form);
      }
      setShowAdd(false);
      setEditingId(null);
      setForm(emptyForm);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    setShowAdd(false);
    setEditingId(null);
    setForm(emptyForm);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/employees/${employeeId}/visas/${deleteTarget.id}`);
      setDeleteTarget(null);
      await load();
    } catch (err: any) {
      setError(err.message || 'Failed to delete');
      setDeleteTarget(null);
    }
  }

  function f(field: keyof CreateVisaDto, value: any) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  if (loading) return <LoadingSpinner message="Loading visas..." />;

  const isEditing = showAdd || editingId !== null;

  return (
    <div className={styles.tabContent}>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <Card>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>
            Visa & Work Permits
            {visas.length > 0 && <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: 400, marginLeft: '8px' }}>({visas.length})</span>}
          </h3>
          {isHR && !isEditing && (
            <button className={styles.addSmallBtn} onClick={startAdd}>+ Add Visa</button>
          )}
        </div>

        {/* Add / Edit form */}
        {isEditing && (
          <div className={styles.inlineForm}>
            <div className={styles.fieldGrid}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Visa Type *</label>
                <select
                  className={styles.formInput}
                  value={form.visaType}
                  onChange={e => f('visaType', e.target.value)}
                >
                  <option value="">Select type...</option>
                  {visaTypes.map(t => (
                    <option key={t.visaDocumentTypeId} value={t.name}>{t.name}</option>
                  ))}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Visa Number</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={form.visaNumber || ''}
                  onChange={e => f('visaNumber', e.target.value || null)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Issuing Country</label>
                <input
                  className={styles.formInput}
                  type="text"
                  value={form.issuingCountry || ''}
                  onChange={e => f('issuingCountry', e.target.value || null)}
                  placeholder="e.g. UAE, Singapore, UK..."
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Status</label>
                <select
                  className={styles.formInput}
                  value={form.status || 'Active'}
                  onChange={e => f('status', e.target.value)}
                >
                  {VISA_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Issue Date</label>
                <input
                  className={styles.formInput}
                  type="date"
                  value={form.issueDate || ''}
                  onChange={e => f('issueDate', e.target.value || null)}
                />
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Expiry Date</label>
                <input
                  className={styles.formInput}
                  type="date"
                  value={form.expiryDate || ''}
                  onChange={e => f('expiryDate', e.target.value || null)}
                />
              </div>
              <div className={styles.formField} style={{ gridColumn: '1 / -1' }}>
                <label className={styles.formLabel}>Notes</label>
                <textarea
                  className={styles.formInput}
                  value={form.notes || ''}
                  onChange={e => f('notes', e.target.value || null)}
                  rows={2}
                  placeholder="Any additional notes..."
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </div>
            <div className={styles.inlineFormActions}>
              <button className={styles.cancelSmallBtn} onClick={handleCancel} disabled={saving}>Cancel</button>
              <button className={styles.saveSmallBtn} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Visa' : 'Add Visa'}
              </button>
            </div>
          </div>
        )}

        {/* Visa list */}
        {visas.length === 0 && !showAdd ? (
          <p style={{ color: '#9CA3AF', textAlign: 'center', padding: '30px 0', fontSize: '14px' }}>
            No visa or work permit records.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: showAdd ? '16px' : '0' }}>
            {visas.map(visa => {
              const badge = getExpiryBadge(visa.expiryDate, visa.status);
              const isExpanded = expandedId === visa.employeeVisaId;
              const isEditingThis = editingId === visa.employeeVisaId;

              return (
                <div
                  key={visa.employeeVisaId}
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    background: isEditingThis ? '#FFFBF0' : '#fff',
                  }}
                >
                  {/* Row header */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      gap: '12px',
                    }}
                    onClick={() => !isEditingThis && setExpandedId(isExpanded ? null : visa.employeeVisaId)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: '#0A2240', fontSize: '14px' }}>{visa.visaType}</div>
                      {visa.visaNumber && <div style={{ color: '#6B7280', fontSize: '13px' }}>#{visa.visaNumber}</div>}
                      {visa.issuingCountry && <div style={{ color: '#6B7280', fontSize: '13px' }}>· {visa.issuingCountry}</div>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                      <span style={{
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#fff',
                        background: badge.colour,
                        whiteSpace: 'nowrap',
                      }}>
                        {badge.label}
                      </span>
                      {visa.expiryDate && (
                        <span style={{ fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap' }}>
                          Exp: {formatDate(visa.expiryDate)}
                        </span>
                      )}
                      <span style={{ fontSize: '12px', color: '#9CA3AF' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && !isEditingThis && (
                    <div style={{
                      borderTop: '1px solid #F0F0F0',
                      padding: '12px 16px',
                      background: '#F9FAFB',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                      gap: '12px',
                      fontSize: '13px',
                    }}>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Issue Date</span>
                        <div style={{ color: '#1F2937', marginTop: '2px' }}>{formatDate(visa.issueDate)}</div>
                      </div>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Expiry Date</span>
                        <div style={{ color: '#1F2937', marginTop: '2px' }}>{formatDate(visa.expiryDate)}</div>
                      </div>
                      <div>
                        <span style={{ color: '#6B7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</span>
                        <div style={{ color: '#1F2937', marginTop: '2px' }}>{visa.status}</div>
                      </div>
                      {visa.linkedDocumentFileName && (
                        <div>
                          <span style={{ color: '#6B7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Linked Document</span>
                          <div style={{ color: '#FF8300', marginTop: '2px', cursor: 'pointer' }}>{visa.linkedDocumentFileName}</div>
                        </div>
                      )}
                      {visa.notes && (
                        <div style={{ gridColumn: '1 / -1' }}>
                          <span style={{ color: '#6B7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notes</span>
                          <div style={{ color: '#1F2937', marginTop: '2px', whiteSpace: 'pre-wrap' }}>{visa.notes}</div>
                        </div>
                      )}
                      <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '8px', borderTop: '1px solid #E5E7EB', paddingTop: '10px', marginTop: '4px' }}>
                        <span style={{ color: '#9CA3AF', fontSize: '11px' }}>Added {formatDate(visa.createdAt)} by {visa.createdBy}</span>
                        {isHR && (
                          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                            <button className={styles.editSmallBtn} onClick={(e) => { e.stopPropagation(); startEdit(visa); }}>Edit</button>
                            <button className={styles.removeBtn} onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: visa.employeeVisaId, name: visa.visaType }); }}>Remove</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Remove Visa Record"
        message={`Remove ${deleteTarget?.name}? This cannot be undone.`}
        confirmLabel="Remove"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
